import { useEffect, useState } from "react";
import api, { fmtEUR } from "@/lib/api";
import { shortConcept } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tag } from "lucide-react";
import { toast } from "sonner";

export function CategorizeDialog({ txs, open, onClose, onDone, categories, branches, sugMap }) {
  const [kind, setKind] = useState("gasto_directo");
  const [categoryId, setCategoryId] = useState("");
  const [branchId, setBranchId] = useState("");

  useEffect(() => {
    if (open) {
      const sug = txs?.length === 1 ? sugMap?.[txs[0].id] : null;
      setKind(sug?.kind || ((txs || []).every((t) => t.amount < 0) ? "gasto_directo" : "liquidacion_tarjeta"));
      setCategoryId(sug?.category_id || "");
      setBranchId(sug?.branch_id || "");
    }
  }, [open, txs, sugMap]);

  if (!txs || txs.length === 0) return null;
  const allExpenses = txs.every((t) => t.amount < 0);
  const allIncome = txs.every((t) => t.amount > 0);
  const mixed = !allExpenses && !allIncome;
  const total = txs.reduce((s, t) => s + Math.abs(t.amount || 0), 0);
  const needsCategory = kind === "gasto_directo" || kind === "otro_ingreso";
  const shownCats = kind === "otro_ingreso"
    ? categories.filter((c) => c.group === "otros_ingresos")
    : categories.filter((c) => c.group !== "otros_ingresos");
  const valid = !mixed && (needsCategory ? !!categoryId : kind === "liquidacion_tarjeta" ? !!branchId : true);

  const confirm = async () => {
    try {
      if (txs.length === 1) {
        await api.post(`/reconciliation/${txs[0].id}/categorize`, {
          kind, category_id: categoryId || undefined, branch_id: branchId || undefined,
        });
        toast.success("Movimiento categorizado");
      } else {
        const r = await api.post("/reconciliation/categorize-bulk", {
          tx_ids: txs.map((t) => t.id), kind,
          category_id: categoryId || undefined, branch_id: branchId || undefined,
        });
        if (r.data.errors?.length) {
          toast.warning(`${r.data.ok} categorizados · ${r.data.errors.length} con error`);
        } else {
          toast.success(`${r.data.ok} movimientos categorizados`);
        }
      }
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al categorizar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="categorize-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Categorizar {txs.length > 1 ? `${txs.length} movimientos` : "movimiento"}</DialogTitle>
          <DialogDescription>
            {txs.length === 1
              ? <>{shortConcept(txs[0].concept)} · <span className="font-num font-semibold">{fmtEUR(txs[0].amount)}</span></>
              : <>Total seleccionado: <span className="font-num font-semibold">{fmtEUR(total)}</span></>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {mixed ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="categorize-mixed-warning">
              La selección mezcla gastos e ingresos. Selecciona solo gastos o solo ingresos para categorizar en lote.
            </p>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-xs">Tipo</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="categorize-kind" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allExpenses ? (
                    <SelectItem value="gasto_directo" data-testid="categorize-kind-gasto">Gasto sin factura (afecta al P&L)</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="liquidacion_tarjeta" data-testid="categorize-kind-tarjeta">Liquidación de tarjeta (ventas TPV)</SelectItem>
                      <SelectItem value="venta_loyverse" data-testid="categorize-kind-loyverse">Ingreso ya contado en Loyverse</SelectItem>
                      <SelectItem value="otro_ingreso" data-testid="categorize-kind-ingreso">Otro ingreso (Delivery, Catering, comisiones...)</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
          {!mixed && needsCategory && (
            <div className="space-y-1.5">
              <Label className="text-xs">{kind === "otro_ingreso" ? "Categoría de ingreso (Delivery, Catering, comisiones recibidas...)" : "Categoría P&L (comisiones, préstamos, nóminas, Seguridad Social, impuestos...)"}</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger data-testid="categorize-category" className="h-9"><SelectValue placeholder="Selecciona categoría..." /></SelectTrigger>
                <SelectContent>
                  {shownCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {!mixed && (
            <div className="space-y-1.5">
              <Label className="text-xs">Sucursal {kind === "liquidacion_tarjeta" ? "(obligatoria)" : "(opcional)"}</Label>
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger data-testid="categorize-branch" className="h-9"><SelectValue placeholder="Selecciona sucursal..." /></SelectTrigger>
                <SelectContent>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button data-testid="categorize-confirm" onClick={confirm} disabled={!valid}
            className="w-full bg-[#D9381E] hover:bg-[#B91C1C] text-white gap-2">
            <Tag className="w-4 h-4" /> Categorizar{txs.length > 1 ? ` ${txs.length}` : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
